import { List } from '@mui/material';
import { MoneyType, mapClientMoneyToServerMoney } from '@razz-team/utils';
import { BreakdownListItem, BreakdownListItemVariant } from '../List/BreakdownListItem';
import { useTranslation } from 'react-i18next';
import { Money } from '../Typography/Money';
import { ErrorListItem, MoneyMath, UiError } from '../..';
import Decimal from 'decimal.js';

export type UiSide = 'BUY' | 'SELL';

export type UiRate = {
  price: Decimal.Value;
  currencyPair: {
    base: string;
    counter: string;
  };
};

export type UiFee = {
  type: string;
  value: MoneyType;
};

export interface TransferBreakdownProps {
  variant: BreakdownListItemVariant;
  side: UiSide | null | undefined;
  amountToDebit?: MoneyType | null;
  fees?: UiFee[] | null;
  amountToConvert?: MoneyType | null;
  rate?: UiRate | null;
  amountToTransfer?: MoneyType | null;
  transactionBreakdown?: {
    total?: {
      isCharged: boolean;
      value: MoneyType;
    };
    exchangeRate?: UiRate;
    transferAmount?: MoneyType;
    topUpAmount?: MoneyType;
    paymentAmount?: MoneyType;
    cashAmount?: MoneyType;
    buyAmount?: MoneyType;
    sellAmount?: MoneyType;
    fee?: {
      type: string;
      value: MoneyType | string;
    };
    operationSide: 'BUY' | 'SELL';
  } | null;
  error?: UiError | string | undefined | null;
}

export interface FeeBreakdownProps {
  variant: BreakdownListItemVariant;
  side: UiSide | null | undefined;
  fees: UiFee[] | null | undefined;
}

export function FeeBreakdown({ variant, side, fees }: FeeBreakdownProps) {
  const { t } = useTranslation();
  if (typeof fees === 'undefined') {
    return null;
  }

  const icon = side === 'SELL' ? 'minus' : 'plus';

  if (fees?.length) {
    return fees.map((fee) => {
      let title;
      switch (fee.type) {
        case 'FX':
          title = t('Exchange fee');
          break;
        case 'TRANSFER':
          title = t('Transfer fee');
          break;
        default:
          title = t('Fee');
          break;
      }

      return (
        <BreakdownListItem
          key={fee.type}
          icon={new Decimal(fee.value.amount).gt(0) ? icon : 'dot'}
          variant={variant}
          title={title}
          loading={false}
          value={<Money value={fee.value} />}
        />
      );
    });
  }

  return (
    <BreakdownListItem
      icon={fees?.length ? icon : 'dot'}
      variant={variant}
      title={t('Fee')}
      loading={fees === null}
      value={fees?.length === 0 ? t('No fee') : undefined}
    />
  );
}

export interface RateBreakdownProps {
  variant?: BreakdownListItemVariant;
  rate: UiRate | null | undefined;
}

export function RateBreakdown({ variant = 'confirm', rate }: RateBreakdownProps) {
  const { t } = useTranslation();

  if (typeof rate === 'undefined') {
    return null;
  }

  return (
    <BreakdownListItem
      icon="multiply"
      variant={variant}
      title={t('Rate')}
      value={
        rate ? (
          <MoneyMath
            leftValue={mapClientMoneyToServerMoney({
              amount: 1,
              currency: rate.currencyPair.base,
            })}
            rightValue={mapClientMoneyToServerMoney(
              {
                amount: rate.price,
                currency: rate.currencyPair.counter,
              },
              false,
            )}
            operator="="
          />
        ) : undefined
      }
      loading={rate === null}
    />
  );
}

export function TransferBreakdown({
  variant,
  side,
  amountToDebit,
  fees,
  amountToConvert,
  rate,
  amountToTransfer,
  transactionBreakdown,
  error,
}: TransferBreakdownProps) {
  const { t } = useTranslation();

  return (
    <List>
      {typeof amountToDebit === 'undefined' ? null : (
        <BreakdownListItem
          icon="dot"
          variant={variant}
          loading={amountToDebit === null}
          title={t('Amount to debit')}
          value={<Money value={amountToDebit} />}
        />
      )}
      {typeof fees === 'undefined' ? null : (
        <FeeBreakdown variant={variant} fees={fees} side={side} />
      )}
      {typeof amountToConvert === 'undefined' ? null : (
        <BreakdownListItem
          icon="equal"
          variant={variant}
          title={t('Amount to convert')}
          value={<Money value={amountToConvert} />}
          loading={amountToConvert === null}
        />
      )}
      {typeof rate === 'undefined' ? null : <RateBreakdown variant={variant} rate={rate} />}
      {typeof amountToTransfer === 'undefined' ? null : (
        <BreakdownListItem
          icon="equal"
          variant={variant}
          title={t('Transfer amount')}
          value={<Money value={amountToTransfer} />}
          loading={amountToTransfer === null}
        />
      )}
      {transactionBreakdown &&
        Object.keys(transactionBreakdown).map((key) => {
          switch (key) {
            case 'total':
              if (transactionBreakdown.total) {
                return (
                  <BreakdownListItem
                    key={key}
                    icon={transactionBreakdown.operationSide === 'SELL' ? 'dot' : 'equal'}
                    variant="confirm"
                    title={
                      transactionBreakdown.total.isCharged
                        ? t('Debited amount')
                        : t('Charge amount')
                    }
                    value={<Money value={transactionBreakdown.total.value} />}
                    loading={false}
                  />
                );
              }
            case 'exchangeRate':
              if (transactionBreakdown.exchangeRate) {
                return (
                  <BreakdownListItem
                    key={key}
                    icon="multiply"
                    variant="confirm"
                    title={t('Rate')}
                    value={
                      <MoneyMath
                        leftValue={mapClientMoneyToServerMoney({
                          amount: 1,
                          currency: transactionBreakdown.exchangeRate.currencyPair.base,
                        })}
                        rightValue={mapClientMoneyToServerMoney({
                          amount: transactionBreakdown.exchangeRate.price,
                          currency: transactionBreakdown.exchangeRate.currencyPair.counter,
                        })}
                        operator="="
                      />
                    }
                    loading={false}
                  />
                );
              }
            case 'transferAmount':
              if (transactionBreakdown.transferAmount) {
                return (
                  <BreakdownListItem
                    key={key}
                    icon={transactionBreakdown.operationSide === 'SELL' ? 'equal' : 'dot'}
                    variant="confirm"
                    title={t('Transfer amount')}
                    value={<Money value={transactionBreakdown.transferAmount} />}
                    loading={false}
                  />
                );
              }
            case 'topUpAmount':
              if (transactionBreakdown.topUpAmount) {
                return (
                  <BreakdownListItem
                    key={key}
                    icon={transactionBreakdown.operationSide === 'SELL' ? 'equal' : 'dot'}
                    variant="confirm"
                    title={t('Top-up amount')}
                    value={<Money value={transactionBreakdown.topUpAmount} />}
                    loading={false}
                  />
                );
              }
            case 'paymentAmount':
              if (transactionBreakdown.paymentAmount) {
                return (
                  <BreakdownListItem
                    key={key}
                    icon={transactionBreakdown.operationSide === 'SELL' ? 'equal' : 'dot'}
                    variant="confirm"
                    title={t('Payment amount')}
                    value={<Money value={transactionBreakdown.paymentAmount} />}
                    loading={false}
                  />
                );
              }
            case 'cashAmount':
              if (transactionBreakdown.cashAmount) {
                return (
                  <BreakdownListItem
                    key={key}
                    icon={transactionBreakdown.operationSide === 'SELL' ? 'equal' : 'dot'}
                    variant="confirm"
                    title={t('Cash amount')}
                    value={<Money value={transactionBreakdown.cashAmount} />}
                    loading={false}
                  />
                );
              }
            case 'buyAmount':
              if (transactionBreakdown.buyAmount) {
                return (
                  <BreakdownListItem
                    key={key}
                    icon="equal"
                    variant="confirm"
                    title={t('Converted amount')}
                    value={<Money value={transactionBreakdown.buyAmount} />}
                    loading={false}
                  />
                );
              }
            case 'sellAmount':
              if (transactionBreakdown.sellAmount) {
                return (
                  <BreakdownListItem
                    key={key}
                    icon="equal"
                    variant="confirm"
                    title={t('Amount to convert')}
                    value={<Money value={transactionBreakdown.sellAmount} />}
                    loading={false}
                  />
                );
              }
            case 'fee':
              if (transactionBreakdown.fee) {
                return (
                  <BreakdownListItem
                    key={key}
                    icon={transactionBreakdown.operationSide === 'SELL' ? 'minus' : 'plus'}
                    variant="confirm"
                    title={t(transactionBreakdown.fee.type)}
                    loading={false}
                    value={
                      typeof transactionBreakdown.fee.value === 'string' ? (
                        transactionBreakdown.fee.value
                      ) : (
                        <Money value={transactionBreakdown.fee.value} />
                      )
                    }
                  />
                );
              }
            default:
              return null;
          }
        })}
      {error ? <ErrorListItem error={error} /> : null}
    </List>
  );
}
